import { Link } from 'react-router';
import Footer from '../components/Footer'
import Nav from '../components/Nav';
import { useState, useEffect, type ReactNode } from 'react';
import { useThemeColors } from '../hooks/useThemeColors';

export default function Terms() {
  const c = useThemeColors();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <div style={{ background: c.bgPrimary, minHeight: '100vh' }}>
      <Nav scrolled={scrolled} />
      <div
        style={{
          maxWidth: 700,
          margin: '0 auto',
          padding: '96px 24px',
        }}
      >
        {/* Label */}
        <p
          style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 11,
            color: c.signal,
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            margin: '0 0 16px',
          }}
        >
          LEGAL
        </p>

        {/* H1 */}
        <h1
          style={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 800,
            fontSize: 48,
            letterSpacing: '-0.03em',
            color: c.textPrimary,
            margin: '0 0 12px',
            lineHeight: 1.1,
          }}
        >
          Terms of Service
        </h1>
        <p
          style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 12,
            color: c.textMuted,
            margin: '0 0 32px',
          }}
        >
          Last updated: August 1, 2026
        </p>

        {/* Body */}
        <TermsBody c={c} />

        {/* Back link */}
        <Link
          to="/primate-vision"
          style={{ color: c.signal, fontSize: 14, textDecoration: 'none', fontWeight: 500 }}
        >
          ← Back to Primate Vision
        </Link>
      </div>
      <Footer />
    </div>
  );
}

function TermsBody({ c }: { c: ReturnType<typeof useThemeColors> }) {
  const H2 = ({ children }: { children: ReactNode }) => (
    <h2
      style={{
        fontFamily: 'Inter, sans-serif',
        fontWeight: 700,
        fontSize: 22,
        letterSpacing: '-0.02em',
        color: c.textPrimary,
        margin: '40px 0 12px',
        lineHeight: 1.3,
      }}
    >
      {children}
    </h2>
  );

  const P = ({ children }: { children: ReactNode }) => (
    <p style={{ color: c.textMuted, fontSize: 15, lineHeight: '1.7', margin: '0 0 12px' }}>
      {children}
    </p>
  );

  const A = ({ href, children }: { href: string; children: ReactNode }) => (
    <a href={href} style={{ color: c.signal, textDecoration: 'none' }}>
      {children}
    </a>
  );

  return (
    <div style={{ marginBottom: 40 }}>
      <P>
        These terms apply to your use of the products and services provided by Primate AI, Inc.
        (doing business as “Primate Intelligence”, “we”, “us”, or “our”) at
        primateintelligence.ai (the “Service”). Our full terms of service are coming soon; the
        section below is effective now.
      </P>

      <H2>Session Recording and Content Use</H2>
      <P>
        When you use the Service’s streaming or analysis features, we record and retain your
        sessions. This includes every prompt you submit and the video processed during a session
        — including frames from your camera or from video you upload — whether or not you use an
        in-product record button, and whether you are signed in or using the Service anonymously.
      </P>
      <P>
        We retain this content and use it to provide and operate the Service and to analyze
        quality and safety. Videos you save to your library, session recordings, and analysis
        results are retained until you delete them from your dashboard. Anonymised analysis
        results (not your original video) may be used to train and improve our models — we
        never use raw video for training without explicit separate consent. We don’t share your
        videos or results with any third party, except as required by law. Session content is stored on
        access-controlled infrastructure and handled as described in our{' '}
        <Link to="/privacy" style={{ color: c.signal, textDecoration: 'none' }}>
          Privacy Policy
        </Link>
        .
      </P>
      <P>
        By starting a streaming session or submitting a prompt, you consent to this recording,
        retention, and use. If you do not agree, do not use the streaming features. To request
        deletion of your account and all data anytime, email{' '}
        <A href="mailto:privacy@primateintelligence.ai">privacy@primateintelligence.ai</A> — processed
        within 30 days. See
        our{' '}
        <Link to="/privacy" style={{ color: c.signal, textDecoration: 'none' }}>
          Privacy Policy
        </Link>{' '}
        for retention and deletion details.
      </P>

      <H2>Contact</H2>
      <P>
        For questions, email{' '}
        <A href="mailto:legal@primateintelligence.ai">legal@primateintelligence.ai</A>
      </P>
    </div>
  );
}
